import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { SelectItem } from "primeng/api";
import { ButtonDirective } from "primeng/button";

import { User } from "../user.model";
import { role } from "../role.const";
import { UserRepository } from "../user.repository";
import { UserService } from "../user.service";
import { CrudAbstractFormComponent } from "../../crud-abstract/crud-abstract-form.component";
import { TeamRepository } from "../../team/team.repository";
import { noop } from "../../shared/noop.function";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { Group } from "../group/group.model";
import { AuthHttpConfig } from "../../security/authorization/auth-http-config";
import { EmailIntegrationType } from "../emailIntegrationType.const";
import { Company } from "app/company/company.model";
import { StringChangeEvent } from "app/ui/dropdown/language-select/language-select.component";
import { NgxPermissionsModule } from "ngx-permissions";
import { Bind } from "primeng/bind";
import { InputText } from "primeng/inputtext";
import { Checkbox } from "primeng/checkbox";
import { Tooltip } from "primeng/tooltip";
import { LanguageSelectComponent } from "../../ui/dropdown/language-select/language-select.component";
import { Select } from "primeng/select";
import { CompanySelectComponent } from "../../ui/dropdown/company-select/company-select.component";
import { UserGroupSelectComponent } from "../../ui/dropdown/user-group-select/user-group-select.component";
import { PasswordComponent } from "../../ui/password/password.component";
import { WysiwygComponent } from "../../ui/wysiwyg/wysiwyg.component";
import { PageTitleComponent } from "../../ui/page-title/page-title.component";
import { UserFormEmailComponent } from "../user-form-email/user-form-email.component";
import { UserFormAiAssistantComponent } from "../user-form-ai-assistant/user-form-ai-assistant.component";
import { UserFormPasswordComponent } from "../user-form-password/user-form-password.component";

@Component({
  selector: "app-user-form",
  templateUrl: "./user-form.component.html",
  styleUrls: ["./user-form.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    NgxPermissionsModule,
    FormsModule,
    ReactiveFormsModule,
    Bind,
    InputText,
    Checkbox,
    Tooltip,
    LanguageSelectComponent,
    Select,
    CompanySelectComponent,
    UserGroupSelectComponent,
    PasswordComponent,
    WysiwygComponent,
    PageTitleComponent,
    UserFormEmailComponent,
    UserFormPasswordComponent,
    UserFormAiAssistantComponent,
  ],
})
export class UserFormComponent
  extends CrudAbstractFormComponent
  implements OnInit
{
  private teamRepository = inject(TeamRepository);
  private userService = inject(UserService);
  private authHttpConfig = inject(AuthHttpConfig);

  public model: User;
  public loggedInUser: User;
  public role = role;
  public loading = false;
  public teamList: SelectItem[] = [];

  constructor() {
    const repository = inject(UserRepository);
    super();
    this.repository = repository;
  }

  ngOnInit() {
    super.ngOnInit();
    this.userService.getUser().then((user) => {
      this.loggedInUser = user;
      this.buildTeamList(this.loggedInUser.getTeam().getOrganization().getId());

      if (!this.model.getId()) {
        this.form.patchValue({
          groups: this.loggedInUser.getGroups(),
          team: this.loggedInUser.getTeam(),
        });
      }
    }, noop);
  }

  public onLanguageChange(event: StringChangeEvent) {
    this.form.get("language").patchValue(event);
  }

  public createForm(): UntypedFormGroup {
    return this.formBuilder.group({
      id: [this.model ? this.model.getId() : ""],
      enabled: [this.model ? this.model.isEnabled() : true],
      limitedCrm: [this.model ? this.model.isLimitedCrm() : false],
      language: [this.model ? this.model.getLanguage() : null],
      email: [this.model ? this.model.getEmail() : "", Validators.required],
      password: [""],
      firstName: [
        this.model ? this.model.getFirstName() : "",
        Validators.required,
      ],
      lastName: [
        this.model ? this.model.getLastName() : "",
        Validators.required,
      ],
      groups: [this.model ? this.model.getGroups() : "", Validators.required],
      team: [this.model ? this.model.getTeam() : "", Validators.required],
      company: [this.model ? this.model.getCompany() : ""],
      ref: [this.model ? this.model.getRef() : ""],
      phone: [this.model ? this.model.getPhone() : ""],
      signature: [this.model ? this.model.getSignature() : ""],
      mailIntegrationEnabled: [
        this.model ? this.model.isMailIntegrationEnabled() : false,
      ],
      mailUsername: [this.model ? this.model.getMailUserName() : ""],
      mailPassword: [""],
      mailImapPath: [this.model ? this.model.getMailImapPath() : ""],
      mailMsGraphFolder: [this.model ? this.model.getMailMsGraphFolder() : ""],
      emailIntegrationType: [
        this.model.getEmailIntegrationType() || EmailIntegrationType.OFFICE_365,
      ],
    });
  }

  public onUserGroupsChange(event: Group[]) {
    this.form.get("groups").patchValue(event);
  }

  public onCompanyChange(event: Company) {
    this.form.get("company").patchValue(event);
  }

  public updateSignature(signature: string) {
    this.form.controls.signature.setValue(signature);
  }

  public prepareSaveModel(form: UntypedFormGroup): User {
    if (
      this.loggedInUser &&
      form.value.groups &&
      !this.loggedInUser.hasRole(role.superAdmin) &&
      form.value.groups.length > 1
    ) {
      this.notifyService.toast(
        TOAST.WARN,
        "Only super admin can set more than one group per user",
      );

      this.form.patchValue({
        groups: [<Group>this.form.value.groups[0]],
      });
    }

    return new User(
      form.value.id,
      form.value.email,
      form.value.email,
      form.value.password,
      null,
      null,
      form.value.roles,
      form.value.groups,
      form.value.firstName,
      form.value.lastName,
      form.value.team,
      form.value.company,
      form.value.phone,
      form.value.ref,
      form.value.signature,
      form.value.enabled,
      form.value.limitedCrm,
      form.value.language,
      form.value.mailIntegrationEnabled,
      form.value.mailUsername,
      form.value.mailPassword,
      form.value.mailImapPath,
      form.value.mailMsGraphFolder,
      form.value.emailIntegrationType,
    );
  }

  public impersonate(user: User): void {
    this.authHttpConfig.setImpersonatedUsername(user.getUsername());
    document.location.href = "/";
  }

  private buildTeamList(orgId) {
    this.subscription.add(
      this.teamRepository.listByOrganization(orgId).subscribe({
        next: (teamList) =>
          teamList.map((team) => {
            this.teamList.push({
              label: team.getName(),
              value: team,
            });
          }),
        error: (err) =>
          this.notifyService.toast(
            TOAST.ERROR,
            "Error",
            "Team list has not been loaded",
          ),
      }),
    );
  }
}
